import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Pagination } from '../pagination';
import Select from '../select';
import Spinner from '../spinner';
import ProductCard from './card';
import { useProductListing } from '../../react-hooks/use-product-listing';
import { scrollToElement } from '../../utils';

function ProductListing({
  addToCardLabel,
  cartEndpoint,
  loginToPurchaseLabel,
  endpoint,
  pageQueryString,
  sortByQueryString,
  itemsPerPageQueryString,
  sortByOptions,
  sortByLabel,
  itemsPerPageOptions,
  itemsPerPageLabel,
  unableToAddToCart,
  pricesLabelsMap
}) {
  const { current: sortByData } = useRef(JSON.parse(sortByOptions));
  const { current: itemsPerPageData } = useRef(JSON.parse(itemsPerPageOptions));
  const { current: pricesLabels } = useRef(JSON.parse(pricesLabelsMap));

  const {
    data,
    error,
    isLoading,
    page,
    setPage,
    sortBy,
    setSortBy,
    itemsPerPage,
    setItemsPerPage
  } = useProductListing({
    endpoint,
    pageQueryString,
    sortByQueryString,
    itemsPerPageQueryString
  });

  function setSelectValue(func, val) {
    scrollToElement('.product-listing');
    setPage('');
    func(val);
  }

  const currentURL = window.location.href;
  const segments = currentURL.split('/');
  const listNameSufix = segments?.[segments.length - 2];

  if (error) return null;

  return (
    <>
      {isLoading ? <Spinner /> : null}
      <div className="product-listing__header">
        <Select
          className="lg-only"
          label={sortByLabel}
          options={sortByData}
          value={sortBy}
          onChange={(value) => setSelectValue(setSortBy, value)}
          hideLabel
        />
        {data?.TotalPages ? (
          <Pagination
            currentPage={page || '1'}
            totalPages={`${data?.TotalPages}`}
            onPageChange={setPage}
          />
        ) : null}
        <Select
          className="lg-only"
          label={itemsPerPageLabel}
          hideLabel
          options={itemsPerPageData}
          value={itemsPerPage}
          onChange={(value) => setSelectValue(setItemsPerPage, value)}
        />
      </div>
      <section aria-label="List of Products" className="product-listing__list">
        {data?.Products?.map(
          ({
            Title,
            Link,
            Author,
            Thumb,
            Prices,
            Description,
            ProductKey,
            IsAuthenticated,
            LoginUrl,
            PublishedDateText,
            SitecoreId,
            IsMember,
            ProductOffSiteUrl,
            ProductImageFlags,
          }, index) => (
            <ProductCard
              Title={Title}
              Link={Link}
              Author={Author}
              Thumb={Thumb}
              Prices={Prices}
              Description={Description}
              ProductKey={ProductKey}
              IsAuthenticated={IsAuthenticated}
              LoginUrl={LoginUrl}
              SitecoreId={SitecoreId}
              IsMember={IsMember}
              PublishedDateText={PublishedDateText}
              ProductOffSiteUrl={ProductOffSiteUrl}
              addToCardLabel={addToCardLabel}
              loginToPurchaseLabel={loginToPurchaseLabel}
              cartEndpoint={cartEndpoint}
              unableToAddToCart={unableToAddToCart}
              pricesLabels={pricesLabels}
              key={index}
              listNameSufix={listNameSufix}
              ProductImageFlags={ProductImageFlags}
            />
          )
        )}
      </section>
      <div className="product-listing__footer">
        <Select
          label={sortByLabel}
          options={sortByData}
          value={sortBy}
          onChange={(value) => setSelectValue(setSortBy, value)}
        />
        {data?.TotalPages ? (
          <Pagination
            className="lg-only"
            currentPage={page || '1'}
            totalPages={`${data?.TotalPages}`}
            onPageChange={setPage}
            hideSummary
          />
        ) : null}
        <Select
          label={itemsPerPageLabel}
          hideLabel
          options={itemsPerPageData}
          value={itemsPerPage}
          onChange={(value) => setSelectValue(setItemsPerPage, value)}
        />
        {data?.TotalPages ? (
          <Pagination
            className="sm-only"
            currentPage={page || '1'}
            totalPages={`${data?.TotalPages}`}
            onPageChange={setPage}
            hideSummary
          />
        ) : null}
      </div>
    </>
  );
}

ProductListing.propTypes = {
  addToCardLabel: PropTypes.string.isRequired,
  unableToAddToCart: PropTypes.string.isRequired,
  cartEndpoint: PropTypes.string.isRequired,
  loginToPurchaseLabel: PropTypes.string.isRequired,
  endpoint: PropTypes.string.isRequired,
  pageQueryString: PropTypes.string,
  sortByQueryString: PropTypes.string,
  itemsPerPageQueryString: PropTypes.string,
  sortByOptions: PropTypes.string,
  sortByLabel: PropTypes.string,
  itemsPerPageOptions: PropTypes.string,
  itemsPerPageLabel: PropTypes.string,
  pricesLabelsMap: PropTypes.string.isRequired
};

export default ProductListing;
